import { PLAN_FACTS, planName } from '@/shared/config/plans';
import { ROUTES } from '@/shared/config/routes';
import { formatMoney, formatMonthDay } from '@/shared/lib/format';
import { summarizeUsage } from '@/shared/lib/pod-usage';
import type { WorkspaceData } from '@/shared/types/workspace';

type PillTone = 'ok' | 'info' | 'warn' | 'off' | 'bad';

export type PlanCard = { name: string; tone: PillTone; status: string; link: string; href: string };
export type ThirdCard = { label: string; value: string; note: string };

/** The Plan card (design: Main, the second card). */
export function planCard(workspace: WorkspaceData): PlanCard {
  const name = planName(workspace.plan);
  const subscription = workspace.billing.subscription;
  if (workspace.plan === 'FREE' || !subscription) {
    return { name, tone: 'off', status: 'No card', link: 'See plans', href: ROUTES.PLANS };
  }
  if (subscription.status === 'PAST_DUE') {
    return {
      name,
      tone: 'bad',
      status: 'Payment failed',
      link: 'Update the card',
      href: ROUTES.BILLING,
    };
  }
  if (subscription.endsAt) {
    return {
      name,
      tone: 'warn',
      status: `Ends ${formatMonthDay(subscription.endsAt)}`,
      link: `Keep ${name}`,
      href: ROUTES.BILLING,
    };
  }
  const renews = subscription.nextBilledAt ?? subscription.currentPeriodEndsAt;
  return {
    name,
    tone: 'ok',
    status: renews ? `Renews ${formatMonthDay(renews)}` : 'Active',
    link: `${formatMoney(subscription.priceCents, subscription.currency, true)} a month`,
    href: ROUTES.BILLING,
  };
}

/** The third card: Pods at a time on Free, extra Pod-hours on a paid plan. */
export function thirdCard(workspace: WorkspaceData): ThirdCard {
  if (workspace.plan === 'FREE') {
    const { PRO, TEAM } = PLAN_FACTS;
    return {
      label: 'Pods at a time',
      value: String(workspace.podsAtATime),
      note: `Pro runs ${PRO.podsAtATime}, Team runs ${TEAM.podsAtATime}`,
    };
  }
  const usage = summarizeUsage(workspace.usage);
  const extraCents = PLAN_FACTS[workspace.plan].extraPodHourCents ?? 0;
  return {
    label: 'Extra Pod-hours',
    value: workspace.usage.extraPodHours > 0 ? usage.extra : '0',
    note:
      workspace.usage.extraCostCents > 0
        ? `${formatMoney(workspace.usage.extraCostCents)} so far, on the next invoice`
        : `${formatMoney(extraCents)} each, on the next invoice`,
  };
}

/** The meter card's last line. */
export function usageLine(workspace: WorkspaceData): string {
  const usage = summarizeUsage(workspace.usage);
  if (usage.isFree) return usage.isOut ? 'All used' : `${usage.left} left, given once`;
  const resets = usage.resetsOn ? `Resets ${usage.resetsOn}` : 'Resets with the next period';
  return usage.isPastIncluded ? `${resets}. ${usage.extra} past the included hours` : resets;
}
